import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "./axios";

// Fetch trader balance and wallet info
export const fetchTraderBalance = createAsyncThunk(
  "traderBalance/fetchBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/trader-balance/balance");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trader balance"
      );
    }
  }
);

// Fetch trader deposit history
export const fetchTraderDeposits = createAsyncThunk(
  "traderBalance/fetchDeposits",
  async ({ status = "", limit = 50, page = 1 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      params.append("limit", limit.toString());
      params.append("page", page.toString());

      const response = await axiosInstance.get(
        `/trader-balance/deposits?${params.toString()}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trader deposits"
      );
    }
  }
);

// Create new trader deposit request
export const createTraderDeposit = createAsyncThunk(
  "traderBalance/createDeposit",
  async ({ amount, txId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/trader-balance/deposits", {
        amount,
        txId: txId || undefined,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create trader deposit"
      );
    }
  }
);

// Get specific deposit details
export const fetchTraderDepositById = createAsyncThunk(
  "traderBalance/fetchDepositById",
  async (depositId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/trader-balance/deposits/${depositId}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch deposit details"
      );
    }
  }
);

// Admin: Assign user to trader
export const assignUserToTrader = createAsyncThunk(
  "traderBalance/assignUser",
  async ({ userId, traderName }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/trader-balance/admin/assign-user",
        {
          userId,
          traderName,
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to assign user to trader"
      );
    }
  }
);

// Admin: Fetch all trader balances
export const fetchAllTraderBalances = createAsyncThunk(
  "traderBalance/fetchAllBalances",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        "/trader-balance/admin/balances"
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all trader balances"
      );
    }
  }
);

// Admin: Fetch trader deposits by trader name
export const fetchTraderDepositsByName = createAsyncThunk(
  "traderBalance/fetchDepositsByName",
  async ({ traderName, status = "" }, { rejectWithValue }) => {
    try {
      const params = status ? `?status=${status}` : "";
      const response = await axiosInstance.get(
        `/trader-balance/admin/deposits/${traderName}${params}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trader deposits"
      );
    }
  }
);

// Fetch trader transaction history (deposits + withdrawals)
export const fetchTraderHistory = createAsyncThunk(
  "traderBalance/fetchHistory",
  async ({ limit = 50, page = 1, type = "" } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      params.append("page", page.toString());
      if (type) params.append("type", type);

      const response = await axiosInstance.get(
        `/trader-balance/history?${params.toString()}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch transaction history"
      );
    }
  }
);

// Create new trader withdrawal request
export const createTraderWithdrawal = createAsyncThunk(
  "traderBalance/createWithdrawal",
  async ({ amount, currency, walletAddress, network }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/trader-balance/withdrawals", {
        amount,
        currency,
        walletAddress,
        network: network || "TRC20",
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create withdrawal request"
      );
    }
  }
);

// Fetch trader withdrawals
export const fetchTraderWithdrawals = createAsyncThunk(
  "traderBalance/fetchWithdrawals",
  async ({ status = "", limit = 50, page = 1 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      params.append("limit", limit.toString());
      params.append("page", page.toString());

      const response = await axiosInstance.get(
        `/trader-balance/withdrawals?${params.toString()}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trader withdrawals"
      );
    }
  }
);

// Test Binance connection (for debugging)
export const testBinanceConnection = createAsyncThunk(
  "traderBalance/testBinance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/trader-balance/test-binance");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to test Binance connection"
      );
    }
  }
);
